import log from "loglevel";
import { whenOpenReplayStarted } from "components/openReplay";

// The one place an error the app caught itself is handed to monitoring. Today that is
// OpenReplay, whose tracker already records uncaught errors and console output on its
// own. An error a React error boundary catches never reaches the window, so without this
// it would not be recorded at all. Add another destination here, not at the call sites.
//
// `where` names the place that caught it (an ErrorBoundary's `name`); everything else in
// `details` travels with the error as metadata.
//
// The console line is written first and always. PostHog's replays keep console output,
// and integration/lazy-chunk-failure.bug-repro.test.js waits for its
// "[reportError] <where>:" prefix.
//
// `reloading: true` (an ErrorBoundary about to reload the page) also keeps a copy in
// sessionStorage until the report is handed over. The report waits for OpenReplay's
// tracker.start(), which waits on a round trip to the ingest server, while a chunk that a
// blocker or proxy rejects fails within milliseconds of page load: the reload can win, and
// the one failure a reload then fixes would never be recorded. The next page load sends
// whatever copy is left (sendReportKeptOverReload). Once the tracker has taken the report
// the copy is dropped; OpenReplay's worker sends its last batch on pagehide itself. If
// handleError throws, the copy stays for the next page, which tries once more: the copy it
// sends is marked `reportedAfterReload` and never kept again.
export const reportError = (error, { where = "app", ...details } = {}) => {
  // OpenReplay's handleError silently drops anything that is not an Error.
  const err = error instanceof Error ? error : new Error(String(error));
  log.error(`[reportError] ${where}: ${err.message}`, err);
  const kept = details.reloading === true && !details.reportedAfterReload
    && keepOverReload(err, where, details);
  whenOpenReplayStarted().then((tracker) => {
    if (!tracker) {
      // Nowhere to hand it (a bot, no project key): the next page has nowhere either.
      if (kept) forgetKept();
      return;
    }
    try {
      tracker.handleError(err, { where, ...details });
      if (kept) forgetKept();
    } catch (handlerError) {
      // Reporting must never be the thing that breaks the page.
      log.warn("reportError: OpenReplay handleError failed", handlerError);
    }
  });
};

export const KEPT_REPORT_KEY = "reportError.keptOverReload";

const keepOverReload = (err, where, details) => {
  try {
    window.sessionStorage.setItem(KEPT_REPORT_KEY, JSON.stringify({
      name: err.name,
      message: err.message,
      stack: err.stack,
      where,
      details,
    }));
    return true;
  } catch {
    // No storage, or the report is over its quota: it goes only where it can right now.
    return false;
  }
};

const forgetKept = () => {
  try {
    window.sessionStorage.removeItem(KEPT_REPORT_KEY);
  } catch {
    // Nothing to forget.
  }
};

// Sends the copy a previous page kept when it reloaded before OpenReplay had started.
// Called once per page load from gatsby-browser.js's onClientEntry. The copy is marked
// `reportedAfterReload`, so it is told apart from the page's own reports and never kept
// again.
export const sendReportKeptOverReload = () => {
  let kept;
  try {
    const raw = window.sessionStorage.getItem(KEPT_REPORT_KEY);
    window.sessionStorage.removeItem(KEPT_REPORT_KEY);
    kept = JSON.parse(raw);
  } catch {
    return;
  }
  if (!kept) return;
  const err = new Error(kept.message);
  err.name = kept.name;
  if (kept.stack) err.stack = kept.stack;
  reportError(err, { ...kept.details, where: kept.where, reportedAfterReload: true });
};
